import { useState, useEffect } from 'react';
import { Save, FileText, Settings as SettingsIcon, Building, Loader2 } from 'lucide-react';
import { useAuthStore } from '@/stores/auth-store';
import { empresasService } from '@/services/empresas.service';
import { satCatalogService } from '@/services/sat-catalog.service';
import { useApiError } from '@/hooks/useApiError';
import { ApiErrorDisplay } from '@/components/ApiErrorDisplay';
import { dialogs } from '@/stores/dialog-store';
import { TaxesSettings } from './components/taxes-settings';
import { Calculator, Wifi, RefreshCw } from 'lucide-react';
import { runCloudSync } from '@/services/cloud-sync.service';

type TabType = 'operation' | 'ticket' | 'fiscal' | 'taxes' | 'network';

export function SettingsPage() {
  const { user, setAuth } = useAuthStore();
  const [activeTab, setActiveTab] = useState<TabType>('operation');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const { error, errorType, handleApiError, handleAuthRedirect, clearError } = useApiError();

  const [formData, setFormData] = useState({
    enablePreSales: false,
    allowNegativeStock: true,
    ticketHeader: '',
    ticketFooter: '',
    legalName: '',
    taxRegime: '',
    taxZipCode: '',
    rfc: '',
  });

  const [taxRegimes, setTaxRegimes] = useState<any[]>([]);
  const [csdFiles, setCsdFiles] = useState({
    cer: null as File | null,
    key: null as File | null,
    password: '',
  });

  const loadCompanySettings = async () => {
    if (!user?.company?.id) return;
    try {
      setLoading(true);
      const company = await empresasService.findById(user.company.id);
      setFormData({
        enablePreSales: company.enablePreSales || false,
        allowNegativeStock: company.allowNegativeStock !== undefined ? company.allowNegativeStock : true,
        ticketHeader: company.ticketHeader || '',
        ticketFooter: company.ticketFooter || '',
        legalName: company.legalName || '',
        taxRegime: company.taxRegime || '',
        taxZipCode: company.taxZipCode || '',
        rfc: company.rfc || '',
      });
      const regimes = await satCatalogService.findTaxRegimes();
      setTaxRegimes(regimes);
      clearError();
    } catch (err) {
      handleApiError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCompanySettings();
  }, [user]);

  const handleSave = async () => {
    if (!user?.company?.id) return;
    setSaving(true);
    try {
      await empresasService.update(user.company.id, formData);
      
      if (csdFiles.cer || csdFiles.key || csdFiles.password) {
        const csdData = new FormData();
        if (csdFiles.cer) csdData.append('cerFile', csdFiles.cer);
        if (csdFiles.key) csdData.append('keyFile', csdFiles.key);
        if (csdFiles.password) csdData.append('password', csdFiles.password);
        await empresasService.uploadCsd(user.company.id, csdData);
      }

      await dialogs.alert('Configuración guardada exitosamente');
      // Update local auth store so user.company reflects new settings immediately
      setAuth(
        { ...user, company: { ...user.company, ...formData } } as any, 
        localStorage.getItem('accessToken') || '',
        localStorage.getItem('refreshToken') || ''
      );
    } catch (err) {
      handleApiError(err);
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'operation' as TabType, label: 'Operación', icon: SettingsIcon },
    { id: 'ticket' as TabType, label: 'Ticket', icon: FileText },
    { id: 'fiscal' as TabType, label: 'Fiscal', icon: Building },
    { id: 'taxes' as TabType, label: 'Impuestos', icon: Calculator },
  ];

  const [isSyncing, setIsSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState<string | null>(null);

  const handleSync = async () => {
    if (!user?.company?.id) return;
    setIsSyncing(true);
    setSyncStatus('Iniciando sincronización...');
    try {
      const result = await runCloudSync({
        onStatus: (msg) => setSyncStatus(msg),
      });

      if (result.ok) {
        if (!result.skipped) {
          setSyncStatus(result.message || 'Sincronización completada exitosamente.');
          await dialogs.alert('Sincronización completada exitosamente. El catálogo local está actualizado.');
        } else {
          setSyncStatus('Ya hay una sincronización en curso...');
        }
      } else {
        setSyncStatus(result.message || 'Error en la sincronización.');
        await dialogs.alert(
          result.message || 'Ocurrió un error al sincronizar. Asegúrate de tener internet.',
          'Error de sincronización',
        );
      }
    } finally {
      setIsSyncing(false);
      setTimeout(() => setSyncStatus(null), 3000);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 size={32} className="animate-spin text-ios-blue" />
      </div>
    );
  }

  return (
    <div className="w-full">
      <ApiErrorDisplay error={error} errorType={errorType} onRetry={loadCompanySettings} onLogin={handleAuthRedirect} />
      
      {!error && (
        <>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold">Configuración de Empresa</h1>
              <p className="text-sm text-ios-gray mt-1">Ajusta cómo funciona tu negocio</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleSync}
                disabled={isSyncing}
                className="ios-button-secondary flex items-center gap-2 border border-blue-500 text-blue-500 hover:bg-blue-50 px-4 py-2 rounded-xl"
              >
                {isSyncing ? <Loader2 size={18} className="animate-spin" /> : null}
                {isSyncing ? 'Sincronizando...' : 'Sincronizar Nube'}
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="ios-button flex items-center gap-2"
              >
                {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                Guardar Cambios
              </button>
            </div>
          </div>

          <div className="ios-card p-0 overflow-hidden">
            <div className="flex border-b border-ios-gray-5">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex items-center justify-center gap-2 py-4 text-sm font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'text-ios-blue border-b-2 border-ios-blue'
                      : 'text-ios-gray hover:text-ios-dark hover:bg-ios-gray-6/50'
                  }`}
                >
                  <tab.icon size={18} />
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="p-6">
              {activeTab === 'operation' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold mb-1">Flujo de Mostrador y Caja</h3>
                    <p className="text-sm text-ios-gray mb-4">
                      Activa esta opción si tu negocio cuenta con vendedores de mostrador que hacen la nota y un cajero independiente que cobra. Si lo desactivas, los vendedores cobrarán directamente en el Punto de Venta.
                    </p>
                    <label className="flex items-center cursor-pointer gap-3 p-4 bg-ios-gray-6 rounded-[12px] border border-ios-gray-5">
                      <div className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${formData.enablePreSales ? 'bg-ios-green' : 'bg-ios-gray-4'}`}>
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${formData.enablePreSales ? 'translate-x-6' : 'translate-x-1'}`} />
                      </div>
                      <input
                        type="checkbox"
                        className="hidden"
                        checked={formData.enablePreSales}
                        onChange={(e) => setFormData({ ...formData, enablePreSales: e.target.checked })}
                      />
                      <span className="font-medium">Habilitar Pre-ventas (Mandado a Caja)</span>
                    </label>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Venta bajo stock</h3>
                    <p className="text-sm text-ios-gray mb-4">
                      Si desactivas esta opción, el sistema bloqueará la venta de productos si la cantidad solicitada supera el stock disponible actual (No permite inventarios negativos).
                    </p>
                    <label className="flex items-center cursor-pointer gap-3 p-4 bg-ios-gray-6 rounded-[12px] border border-ios-gray-5">
                      <div className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${formData.allowNegativeStock ? 'bg-ios-green' : 'bg-ios-gray-4'}`}>
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${formData.allowNegativeStock ? 'translate-x-6' : 'translate-x-1'}`} />
                      </div>
                      <input
                        type="checkbox"
                        className="hidden"
                        checked={formData.allowNegativeStock}
                        onChange={(e) => setFormData({ ...formData, allowNegativeStock: e.target.checked })}
                      />
                      <span className="font-medium">Permitir venta bajo stock (negativos)</span>
                    </label>
                  </div>
                </div>
              )}

              {activeTab === 'ticket' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Encabezado del Ticket</label>
                    <p className="text-xs text-ios-gray mb-2">Aparecerá en la parte superior. Puedes incluir tu slogan, teléfonos o mensaje de bienvenida.</p>
                    <textarea
                      value={formData.ticketHeader}
                      onChange={(e) => setFormData({ ...formData, ticketHeader: e.target.value })}
                      placeholder="¡Bienvenidos a nuestra tienda!..."
                      className="ios-input w-full min-h-[100px] resize-y"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Pie del Ticket (Garantías / Despedida)</label>
                    <p className="text-xs text-ios-gray mb-2">Aparecerá debajo del total. Ideal para políticas de devolución o agradecimientos.</p>
                    <textarea
                      value={formData.ticketFooter}
                      onChange={(e) => setFormData({ ...formData, ticketFooter: e.target.value })}
                      placeholder="No se aceptan devoluciones después de 30 días..."
                      className="ios-input w-full min-h-[100px] resize-y"
                    />
                  </div>
                </div>
              )}

              {activeTab === 'fiscal' && (
                <div className="space-y-8">
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Datos de Facturación</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="col-span-1 md:col-span-2">
                        <label className="block text-sm font-medium mb-1">Razón Social</label>
                        <input
                          type="text"
                          value={formData.legalName}
                          onChange={(e) => setFormData({ ...formData, legalName: e.target.value })}
                          className="ios-input w-full"
                          placeholder="EMPRESA SA DE CV"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">RFC / NIT</label>
                        <input
                          type="text"
                          value={formData.rfc}
                          onChange={(e) => setFormData({ ...formData, rfc: e.target.value })}
                          className="ios-input w-full uppercase"
                          placeholder="XAXX010101000"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Código Postal (Lugar de Expedición)</label>
                        <input
                          type="text"
                          value={formData.taxZipCode}
                          onChange={(e) => setFormData({ ...formData, taxZipCode: e.target.value })}
                          className="ios-input w-full"
                          placeholder="12345"
                          maxLength={5}
                        />
                      </div>
                      <div className="col-span-1 md:col-span-2">
                        <label className="block text-sm font-medium mb-1">Régimen Fiscal (Catálogo SAT)</label>
                        <select
                          value={formData.taxRegime}
                          onChange={(e) => setFormData({ ...formData, taxRegime: e.target.value })}
                          className="ios-input w-full"
                        >
                          <option value="">Selecciona un régimen...</option>
                          {taxRegimes.map(r => (
                            <option key={r.code} value={r.code}>{r.code} - {r.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                  
                  <div className="pt-6 border-t border-ios-gray-5">
                    <h3 className="text-lg font-semibold mb-4">Certificados de Sello Digital (CSD)</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">Archivo .CER</label>
                        <input
                          type="file"
                          accept=".cer"
                          onChange={(e) => setCsdFiles(prev => ({ ...prev, cer: e.target.files?.[0] || null }))}
                          className="w-full text-sm text-ios-gray file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-ios-blue/10 file:text-ios-blue hover:file:bg-ios-blue/20 cursor-pointer"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Archivo .KEY</label>
                        <input
                          type="file"
                          accept=".key"
                          onChange={(e) => setCsdFiles(prev => ({ ...prev, key: e.target.files?.[0] || null }))}
                          className="w-full text-sm text-ios-gray file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-ios-blue/10 file:text-ios-blue hover:file:bg-ios-blue/20 cursor-pointer"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Contraseña de la Clave Privada</label>
                        <input
                          type="password"
                          value={csdFiles.password}
                          onChange={(e) => setCsdFiles(prev => ({ ...prev, password: e.target.value }))}
                          className="ios-input w-full"
                          placeholder="••••••••"
                        />
                        <p className="text-xs text-ios-gray mt-1">Solo captura esto si vas a actualizar tus certificados.</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'taxes' && (
                <div className="pt-2">
                  <TaxesSettings />
                </div>
              )}

            </div>
          </div>
        </>
      )}
    </div>
  );
}
